import { FormItemProps as AntFormItemProps } from 'antd';
import { isNil } from 'rambda';
import {
  isDiameterNominal,
  isElectricityDeviceCategory,
  isElectricityMeasurementCategory,
  isElectricityMeterMeasurementType,
  isGSize,
  isGasDeviceCategory,
  isGasMeasurementCategory,
  isInstallationPosition,
  isMounting,
  isOrientation,
  isOtherDeviceCategory,
  isPhase,
  isResistance,
  isTubeMaterial,
  isWaterMeterMeasurementType,
} from '@enmon/commons';
import { isOutputTechnology } from '@enmon/commons/lib/tools';
import type { MeterModelSuggestions, SuggestedValue } from '../../hooks/meter_models/useMeterModelDetails';

/**
 * Helper function to extract the field key from form name path
 *
 * It Handles array format with detail or registers prefix like:
 * ["detail", "i_ref"], ["detail", "width", "value"] or ["registers", 0,"factor"]
 *
 * where in detail we care about the second item as field key
 * and in registers we care about the third item as field key.
 */
const extractFieldKeyFromFormName = (name: AntFormItemProps['name']) => {
  if (!name || typeof name === 'number') return null;

  if (typeof name === 'string') return name;

  const firstItem = name[0];

  if (firstItem === 'detail') {
    const detail = name[1];
    return typeof detail === 'string' ? detail : null;
  }

  const registerField = name[2];
  // We only care about 'factor' field in registers for now
  return typeof registerField === 'string' && registerField === 'factor' ? 'pulse_rate' : null;
};

// Helper to filter out wrong data in details on meter model
const validateSuggestedValue = (fieldKey: keyof MeterModelSuggestions, suggestedValue: SuggestedValue) => {
  switch (fieldKey) {
    case 'circuitBreakerSize':
      return typeof suggestedValue === 'string';

    // Boolean fields are treated as numbers here
    case 'isDirect':
    case 'flange':
    case 'flowrate':
    case 'hotWater':
    case 'iMax':
    case 'iMin':
    case 'iRef':
    case 'layLength':
    case 'led_rate':
    case 'pMax':
    case 'programmable_pulses':
    case 'pulse_rate':
    case 'qMax':
    case 'qMin':
    case 'tariffs':
    case 'width':
    case 'woltman':
      return typeof suggestedValue === 'number';

    case 'deviceCategory':
      return (
        isGasDeviceCategory(suggestedValue) ||
        isElectricityDeviceCategory(suggestedValue) ||
        isOtherDeviceCategory(suggestedValue)
      );

    case 'diameterNominal':
      return isDiameterNominal(suggestedValue);
    case 'gSize':
      return isGSize(suggestedValue);
    case 'inbuilt_outputs':
      return isOutputTechnology(suggestedValue);
    case 'installationPosition':
      return isInstallationPosition(suggestedValue);
    case 'measurementCategory':
      return isElectricityMeasurementCategory(suggestedValue) || isGasMeasurementCategory(suggestedValue);
    case 'measurementType':
      return isElectricityMeterMeasurementType(suggestedValue) || isWaterMeterMeasurementType(suggestedValue);
    case 'mounting':
      return isMounting(suggestedValue);
    case 'orientation':
      return isOrientation(suggestedValue);
    case 'phases':
      return isPhase(suggestedValue);
    case 'resistance':
      return isResistance(suggestedValue);
    case 'tubeMaterial':
      return isTubeMaterial(suggestedValue);
    default:
      console.warn(`Unknown field key: ${fieldKey} with value:`, suggestedValue);
      return false;
  }
};

interface SuggestionConfigShape {
  fieldKey: string | null;
  suggestedValue: SuggestedValue | null;
}

export const getSuggestionConfig = (
  name: AntFormItemProps['name'],
  meterModelSuggestions?: MeterModelSuggestions,
): SuggestionConfigShape => {
  if (!name || !meterModelSuggestions) return { fieldKey: null, suggestedValue: null };

  const fieldKey = extractFieldKeyFromFormName(name) as keyof MeterModelSuggestions | null;
  if (isNil(fieldKey)) return { fieldKey: null, suggestedValue: null };

  const fieldSuggestion = fieldKey in meterModelSuggestions ? meterModelSuggestions[fieldKey] : null;
  if (isNil(fieldSuggestion)) return { fieldKey, suggestedValue: null };

  const suggestedValue =
    typeof fieldSuggestion === 'object' ? fieldSuggestion.value : (fieldSuggestion as SuggestedValue);

  return { fieldKey, suggestedValue: validateSuggestedValue(fieldKey, suggestedValue) ? suggestedValue : null };
};
